import { Provider } from '@angular/core';
import { DEFAULT_CLIENT_ID, DEFAULT_VENDOR_ID } from '../../http/api-client.tokens';

/**
 * Tenant configuration loaded from environment.ts files. This allows runtime configuration for deployment scenarios
 * like Azure Static Web Apps.
 */
export interface TenantConfig {
  clientId?: string;
  vendorId?: string;
}

/**
 * Provides Tenant IDs (Client ID & Vendor ID) from environment configuration object.
 *
 * This replaces `provideApiInfrastructureWithTenantHeaders()` for runtime-configurable deployments. Loads tenant IDs
 * from environment object in environment.ts files instead of webpack-injected globals.
 *
 * Usage in app.config.ts:
 *
 * ```typescript
 * import { environment } from '../environments/environment';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideTenantIdsFromEnvironment(environment),
 *     // ... other providers
 *   ],
 * };
 * ```
 *
 * @param environment - Environment object from environment.ts that contains tenantConfig or direct clientId/vendorId
 * @returns Array of providers for DI token injection
 */
export function provideTenantIdsFromEnvironment(environment: any): Provider[] {
  // Support both formats:
  // 1. { clientId: '1', vendorId: '6' } - direct properties
  // 2. { tenantConfig: { clientId: '1', vendorId: '6' } } - nested object

  const tenantConfig = environment.tenantConfig || {
    clientId: environment.clientId,
    vendorId: environment.vendorId,
  };

  return [
    {
      provide: DEFAULT_CLIENT_ID,
      useValue: tenantConfig.clientId ?? '1',
    },
    {
      provide: DEFAULT_VENDOR_ID,
      useValue: tenantConfig.vendorId ?? '6',
    },
  ];
}
